import { makeObservable, observable, action, runInAction, reaction } from 'mobx';
import { searchRuns, getRunById, type RunSummary, type RunDetail } from '@/api/validationRuns';
import type { RootStore } from './RootStore';

// Live feed of aivalidation.ValidationRun rows. Merchant-scoped via the
// backend (which joins through ai.AssistantAI).

// localStorage key for the sticky agent filter. Per-page key so the
// scenario library and recent runs pages can each remember their own
// last selection independently — different mental models, different
// expectations of "where I left off."
const AGENT_FILTER_STORAGE_KEY = 'agentstudio.recentRuns.lastAgentId';

function loadStoredAgentFilter(): number | null {
  try {
    const raw = window.localStorage.getItem(AGENT_FILTER_STORAGE_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredAgentFilter(v: number | null): void {
  try {
    if (v == null) window.localStorage.removeItem(AGENT_FILTER_STORAGE_KEY);
    else window.localStorage.setItem(AGENT_FILTER_STORAGE_KEY, String(v));
  } catch {
    // localStorage can throw in private mode / quota exceeded — non-fatal.
  }
}

export class ValidationRunsStore {
  @observable runs: RunSummary[] = [];
  @observable isLoading = false;
  @observable error: string | null = null;

  @observable days = 30;
  // Sticky across browser sessions — engineers iterating on one agent
  // shouldn't have to re-pick it every time they come back to the page.
  @observable agentFilter: number | null = loadStoredAgentFilter();
  @observable environmentFilter: string | null = null;
  @observable statusFilter: string | null = null;

  // Detail (lazy)
  @observable openRun: RunDetail | null = null;
  @observable isLoadingRun = false;
  @observable runError: string | null = null;

  constructor(private root: RootStore) {
    makeObservable(this);

    reaction(
      () => this.root.merchantContextStore.selectedMerchantId,
      (merchantId) => {
        if (merchantId != null) {
          void this.fetch();
        } else {
          runInAction(() => {
            this.runs = [];
          });
        }
      },
      { fireImmediately: true }
    );
  }

  @action setDays(v: number): void {
    this.days = v;
    void this.fetch();
  }

  @action setAgentFilter(v: number | null): void {
    this.agentFilter = v;
    saveStoredAgentFilter(v);
    void this.fetch();
  }

  @action setEnvironmentFilter(v: string | null): void {
    this.environmentFilter = v;
    void this.fetch();
  }

  @action setStatusFilter(v: string | null): void {
    this.statusFilter = v;
    void this.fetch();
  }

  @action
  async fetch(): Promise<void> {
    if (this.root.merchantContextStore.selectedMerchantId == null) return;
    this.isLoading = true;
    this.error = null;
    try {
      const items = await searchRuns({
        days: this.days,
        agentId: this.agentFilter ?? undefined,
        environment: this.environmentFilter ?? undefined,
        status: this.statusFilter ?? undefined,
        pageSize: 100,
      });
      runInAction(() => {
        this.runs = items;
        this.isLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoading = false;
        this.error = err instanceof Error ? err.message : 'Failed to load runs';
      });
    }
  }

  @action
  async loadRun(id: number): Promise<void> {
    this.isLoadingRun = true;
    this.runError = null;
    this.openRun = null;
    try {
      const detail = await getRunById(id);
      runInAction(() => {
        this.openRun = detail;
        this.isLoadingRun = false;
      });
    } catch (err) {
      runInAction(() => {
        this.isLoadingRun = false;
        this.runError = err instanceof Error ? err.message : 'Failed to load run';
      });
    }
  }

  @action
  closeRun(): void {
    this.openRun = null;
    this.runError = null;
  }
}
